import React, { useState, useEffect } from 'react';
import { useDynamicDefinition } from '../core/hooks/useDynamicDefinition';
import { useDynamicEntitiesContext } from '../core/DynamicEntitiesProvider';
import { useDynamicEntitiesApi } from '../core/hooks/useDynamicEntitiesApi';

interface DynamicEntityGridProps {
    featureCode: string;
    entityCode: string;
    masterRecordId: string;
    onEdit?: (idEntityMultiple: string) => void;
    onCreate?: () => void;
    className?: string;
}

export const DynamicEntityGrid: React.FC<DynamicEntityGridProps> = ({
    featureCode,
    entityCode,
    masterRecordId,
    onEdit,
    onCreate,
    className
}) => {
    const { baseUrl, onError } = useDynamicEntitiesContext();
    const api = useDynamicEntitiesApi();
    const { definition, loading: defLoading, error: defError } = useDynamicDefinition(featureCode, entityCode);
    const [records, setRecords] = useState<Record<string, any>[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchRecords = async () => {
        if (!baseUrl || !featureCode || !entityCode || !masterRecordId) return;
        setLoading(true);
        try {
            const data = await api.getMultipleRecords(featureCode, entityCode, masterRecordId);
            const flatRecords = data.map((d: any) => d.values);
            setRecords(flatRecords);
        } catch (err) {
            if (onError) onError(err as Error);
            else console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRecords();
    }, [api, featureCode, entityCode, masterRecordId]);

    const handleDelete = async (idEntityMultiple: string) => {
        if (!confirm('¿Está seguro de que desea eliminar este registro?')) return;
        try {
            await api.deleteRecord(featureCode, masterRecordId, idEntityMultiple);
            fetchRecords();
        } catch (err) {
            if (onError) onError(err as Error);
            else alert('Error al eliminar');
        }
    };

    if (defLoading || loading && records.length === 0) return <div>Cargando...</div>;
    if (defError) return <div>Error cargando la definición</div>;
    if (!definition) return <div>Sin definición</div>;

    const columns = definition.fields
        .filter(f => f.visible)
        .sort((a, b) => a.order - b.order);

    return (
        <div className={`bg-white dark:bg-dark-bg-primary rounded border border-gray-200 dark:border-gray-800 text-sm ${className || ''}`}>
            <div className="p-4 flex justify-between items-center bg-gray-50 border-b">
                <h3 className="font-bold">{definition.description || entityCode}</h3>
                <button
                    onClick={onCreate}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Añadir Registro
                </button>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-100 border-b">
                            {columns.map(col => (
                                <th key={col.id} className="p-3 font-semibold text-gray-700">
                                    {col.labels?.['es'] || col.code}
                                </th>
                            ))}
                            <th className="p-3 text-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {records.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length + 1} className="p-4 text-center text-gray-500">
                                    No se encontraron registros.
                                </td>
                            </tr>
                        ) : (
                            records.map((record) => (
                                <tr key={record.id} className="border-b hover:bg-gray-50">
                                    {columns.map(col => (
                                        <td key={col.id} className="p-3">
                                            {record[col.code]}
                                        </td>
                                    ))}
                                    <td className="p-3 text-right space-x-2">
                                        <button
                                            onClick={() => onEdit && record.id && onEdit(record.id)}
                                            className="text-blue-600 hover:text-blue-800"
                                        >
                                            Editar
                                        </button>
                                        <button
                                            onClick={() => record.id && handleDelete(record.id)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            Eliminar
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
